/**
 * Visualizer Registry and Manager
 * Handles registration and switching between different visualization types
 *
 * Supports three sources of visualizers:
 * 1. Bundled (free tier) - bars, bubbles - loaded via static import
 * 2. Premium - loaded from Supabase Storage with tier-based access control
 * 3. User-generated - loaded from user's Supabase Storage folder
 */

import { visualizerLoader, AccessDeniedError } from './lib/visualizer-loader.js';
import { Base2DVisualizer } from './lib/base-2d-visualizer.js';
import { BaseWebGPUVisualizer } from './lib/base-webgpu-visualizer.js';
import { BaseWebGLVisualizer } from './lib/base-webgl-visualizer.js';
import { sharedGeometryManager } from './lib/shared-geometry-manager.js';
import * as THREE from 'three/webgpu';
import * as THREE_WEBGL from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { MarchingCubes } from 'three/addons/objects/MarchingCubes.js';

// Expose base classes, utilities, and THREE globally for dynamically loaded visualizers
// (blob URLs can't resolve relative imports — premium & user-generated visualizers use these)
window.VisualizerBases = {
  Base2DVisualizer,
  BaseWebGPUVisualizer,
  BaseWebGLVisualizer,
  OrbitControls,
  MarchingCubes,
  sharedGeometryManager,
};
window.THREE = THREE;            // WebGPU build — for WebGPU visualizers
window.THREE_WEBGL = THREE_WEBGL; // Standard build — for WebGL visualizers (e.g. metaballs)

// Bundled visualizers (free tier) - loaded via static import
const BUNDLED_VISUALIZERS = ['bars', 'bubbles', 'bands-meter', 'spectral-analyzer', 'beat-pulse', 'semitone-piano', 'dancing-figure', 'fireworks', 'color-explosion', 'laser-fan-rig', 'neon-lightning-field', 'aurora-ribbons', 'shockwave-bloom', 'glitch-prism-grid', 'starfield-drift', 'breathing-nebula', 'koi-pond-ripples', 'sleep-aurora-curtains', 'lux-pond', 'mandala-bloom', 'mosaic-slideshow', 'deep-star-cathedral', 'iridescent-tile-pusher'];

// Local module paths for development (bypasses Supabase Storage)
// In production, these are loaded from Supabase Storage
const DEV_MODULE_PATHS = {
  'spheres3d-webgpu': './visualizations/premium/spheres3d-webgpu.js',
  'pointlights': './visualizations/premium/pointlights.js',
  'particle-universe': './visualizations/premium/particle-universe.js',
  'blob': './visualizations/premium/blob.js',
  'metaballs': './visualizations/premium/metaballs.js',
  'linked-particles': './visualizations/premium/linked-particles.js'
};

// VisualizerRegistry singleton - also exposed on window for debugging
const VisualizerRegistry = {
  visualizers: {},
  loadingPromises: {}, // Track in-progress loads to avoid duplicates
  _listCache: null,    // Cache for getList() results
  _listCacheTime: 0,   // Cache timestamp
  _hiddenVisualizersCache: null, // Cache for hidden visualizer names (Set)

  /**
   * Register a visualization class
   */
  register(VisualizerClass) {
    const name = VisualizerClass.name;
    this.visualizers[name] = VisualizerClass;
    // Invalidate list cache when new visualizer is registered
    this._listCache = null;
  },

  /**
   * Get a visualization class by name (sync - returns null if not loaded yet)
   */
  get(name) {
    return this.visualizers[name];
  },

  /**
   * Load a visualization class by name (async - loads module if needed)
   * This prevents loading multiple Three.js builds at page start
   *
   * @param {string} name - Visualizer name
   * @param {string} source - Optional: 'bundled', 'premium', 'user', or 'auto'
   * @throws {AccessDeniedError} if user doesn't have tier access to premium visualizer
   */
  async load(name, source = 'auto') {
    // Already loaded - return immediately
    if (this.visualizers[name]) {
      return this.visualizers[name];
    }

    // Avoid duplicate concurrent loads
    if (this.loadingPromises[name]) {
      await this.loadingPromises[name];
      return this.visualizers[name];
    }

    // Determine source if auto
    if (source === 'auto') {
      if (BUNDLED_VISUALIZERS.includes(name)) {
        source = 'bundled';
      } else if (DEV_MODULE_PATHS[name] || visualizerLoader.getPremiumManifest()[name]) {
        source = 'premium';
      } else {
        source = 'user';
      }
    }

    // Load based on source
    let loadPromise;

    switch (source) {
      case 'bundled':
        // Bundled visualizers are already registered via static imports
        // If we get here, something is wrong
        console.warn(`Bundled visualizer "${name}" not registered. Check imports.`);
        return null;

      case 'premium':
        loadPromise = this._loadPremiumVisualizer(name);
        break;

      case 'user':
        loadPromise = this._loadUserVisualizer(name);
        break;

      default:
        console.error(`Unknown visualizer source: ${source}`);
        return null;
    }

    this.loadingPromises[name] = loadPromise;

    try {
      await loadPromise;
    } finally {
      delete this.loadingPromises[name];
    }

    return this.visualizers[name];
  },

  /**
   * Load a premium visualizer
   * Uses local files in development, Supabase Storage in production
   */
  async _loadPremiumVisualizer(name) {
    // In development, load from local files
    if (!visualizerLoader.shouldUseStorage() && DEV_MODULE_PATHS[name]) {
      const module = await import(DEV_MODULE_PATHS[name]);
      // Register the visualizer class (premium visualizers don't self-register)
      if (module.default) {
        this.register(module.default);
      }
      return;
    }

    // In production, load from Supabase Storage
    try {
      const module = await visualizerLoader.loadPremiumVisualizer(name);

      // Register the visualizer class
      if (module.default) {
        this.register(module.default);
      }
    } catch (error) {
      if (error instanceof AccessDeniedError) {
        throw error; // Re-throw access errors for UI to handle
      }
      console.error(`Failed to load premium visualizer "${name}":`, error);
      throw error;
    }
  },

  /**
   * Load a user-generated visualizer from Supabase Storage
   */
  async _loadUserVisualizer(nameOrId) {
    try {
      const module = await visualizerLoader.loadUserVisualizer(nameOrId);

      // Register the visualizer class
      if (module.default) {
        this.register(module.default);
      }
    } catch (error) {
      console.error(`Failed to load user visualizer "${nameOrId}":`, error);
      throw error;
    }
  },

  /**
   * Fetch hidden visualizer names from the database
   * Returns a Set of visualizer names that are hidden by admin
   * @returns {Promise<Set<string>>}
   */
  async fetchHiddenSet() {
    if (this._hiddenVisualizersCache) return this._hiddenVisualizersCache;

    try {
      const supabase = visualizerLoader.getSupabaseClient();
      if (!supabase) return new Set();

      const { data, error } = await supabase.rpc('get_hidden_visualizers');
      if (error) {
        console.warn('[VisualizerRegistry] Failed to fetch hidden visualizers:', error.message);
        return new Set();
      }

      this._hiddenVisualizersCache = new Set(Array.isArray(data) ? data : []);
      return this._hiddenVisualizersCache;
    } catch (err) {
      console.warn('[VisualizerRegistry] Error fetching hidden visualizers:', err);
      return new Set();
    }
  },

  /**
   * Get all registered visualizations
   */
  getAll() {
    return Object.values(this.visualizers);
  },

  /**
   * Get list of visualization names and display names
   * Includes bundled, premium, and user visualizers
   *
   * @param {boolean} forceRefresh - Force refresh of cached list
   * @param {boolean} isAdmin - Whether user is admin (shows admin-only visualizers)
   * @returns {Promise<Array>} - List of visualizer info objects
   */
  async getList(forceRefresh = false, isAdmin = false) {
    // Return cached list if fresh (within 30 seconds)
    const cacheAge = Date.now() - this._listCacheTime;
    if (!forceRefresh && this._listCache && cacheAge < 30000) {
      // Filter cached list for non-admins
      return isAdmin ? this._listCache : this._listCache.filter(v => !v.adminOnly && !v.hidden);
    }

    // Invalidate hidden set cache on force refresh so it re-fetches
    if (forceRefresh) {
      this._hiddenVisualizersCache = null;
    }

    // Fetch hidden set from database
    const hiddenSet = await this.fetchHiddenSet();

    const list = [];

    // 1. Add bundled visualizers (always available)
    for (const name of BUNDLED_VISUALIZERS) {
      const cls = this.visualizers[name];
      list.push({
        name,
        displayName: cls?.displayName || name,
        source: 'bundled',
        premium: false,
        loaded: !!cls,
        adminOnly: cls?.adminOnly || false,
        hidden: hiddenSet.has(name)
      });
    }

    // 2. Add premium visualizers from manifest
    const premiumManifest = visualizerLoader.getPremiumManifest();
    for (const [name, info] of Object.entries(premiumManifest)) {
      list.push({
        name,
        displayName: info.displayName,
        source: 'premium',
        premium: true,
        tier: info.tier,
        contextType: info.contextType,
        loaded: !!this.visualizers[name],
        adminOnly: info.adminOnly || false,
        hidden: hiddenSet.has(name)
      });
    }

    // 3. Add user-generated visualizers (includes user's own + public visualizers)
    try {
      const userVisualizers = await visualizerLoader.getUserVisualizers();
      for (const viz of userVisualizers) {
        list.push({
          name: viz.name,
          displayName: viz.display_name,
          source: 'user',
          premium: false,
          id: viz.id,
          user_id: viz.user_id,  // Include user_id for ownership checks
          contextType: viz.context_type,
          loaded: !!this.visualizers[viz.name],
          adminOnly: false,  // User visualizers are never admin-only
          hidden: hiddenSet.has(viz.name)
        });
      }
    } catch (error) {
      // User visualizers are optional, don't fail if unavailable
      console.warn('[VisualizerRegistry] Could not load user visualizers:', error.message);
    }

    // Cache the list
    this._listCache = list;
    this._listCacheTime = Date.now();

    // Filter for non-admins (hide adminOnly and hidden visualizers)
    return isAdmin ? list : list.filter(v => !v.adminOnly && !v.hidden);
  },

  /**
   * Synchronous version of getList for backward compatibility
   * Returns only currently known visualizers (may not include user visualizers)
   * @param {boolean} isAdmin - Whether user is admin (shows admin-only visualizers)
   */
  getListSync(isAdmin = false) {
    const hiddenSet = this._hiddenVisualizersCache || new Set();
    const list = [];

    // Bundled visualizers
    for (const name of BUNDLED_VISUALIZERS) {
      const cls = this.visualizers[name];
      list.push({
        name,
        displayName: cls?.displayName || name,
        source: 'bundled',
        premium: false,
        adminOnly: cls?.adminOnly || false,
        hidden: hiddenSet.has(name)
      });
    }

    // Premium visualizers from manifest
    try {
      const premiumManifest = visualizerLoader.getPremiumManifest();
      for (const [name, info] of Object.entries(premiumManifest)) {
        list.push({
          name,
          displayName: info.displayName,
          source: 'premium',
          premium: true,
          tier: info.tier,
          adminOnly: info.adminOnly || false,
          hidden: hiddenSet.has(name)
        });
      }
    } catch (err) {
      console.warn('[VisualizerRegistry] Error loading premium manifest:', err);
    }

    // Filter for non-admins (hide adminOnly and hidden visualizers)
    return isAdmin ? list : list.filter(v => !v.adminOnly && !v.hidden);
  },

  /**
   * Clear caches (call on auth state change)
   */
  clearCache() {
    this._listCache = null;
    this._listCacheTime = 0;
    this._hiddenVisualizersCache = null;
    visualizerLoader.clearUserVisualizersCache();
  },

  /**
   * Set the Supabase client for the loader
   */
  setSupabaseClient(client) {
    visualizerLoader.setSupabaseClient(client);
  }
};

// Expose on window for debugging and backward compatibility
if (typeof window !== 'undefined') {
  window.VisualizerRegistry = VisualizerRegistry;
}

/**
 * Visualizer Manager - Manages the active visualization
 */
class VisualizerManager {
  // Private abort controller for cancelling in-flight setType() calls
  #setTypeAbortController = null;

  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.canvasContainer = canvas.parentElement;
    this.currentVisualizer = null;
    this.currentType = null;
    this.currentContextType = null; // '2d', 'webgl', 'webgpu', etc.
    this.config = options;
    // Track logical dimensions (before pixel ratio scaling)
    this.logicalWidth = canvas.width;
    this.logicalHeight = canvas.height;
    // Callback for visualizer change events (e.g., to reset audio feature state)
    this._onVisualizerChange = null;
  }

  /**
   * Set a callback to be invoked when the visualizer changes.
   * Used to reset temporal audio feature state (onset detection history).
   * @param {Function|null} callback - Called with (newTypeName, oldTypeName)
   */
  setOnVisualizerChange(callback) {
    this._onVisualizerChange = callback;
  }

  /**
   * Set the logical canvas dimensions
   */
  setDimensions(width, height) {
    this.logicalWidth = width;
    this.logicalHeight = height;
    this.canvas.width = width;
    this.canvas.height = height;
  }

  /**
   * Get logical dimensions (unaffected by pixel ratio scaling)
   */
  getLogicalDimensions() {
    return { width: this.logicalWidth, height: this.logicalHeight };
  }

  /**
   * Switch to a different visualization type
   * @param {string} typeName - Name of the visualizer
   * @param {string} source - Optional: 'bundled', 'premium', 'user', or 'auto'
   * @returns {Promise<boolean>} - true if switch was successful, false if cancelled or failed
   * @throws {AccessDeniedError} if user doesn't have access
   */
  async setType(typeName, source = 'auto') {
    // Cancel any in-flight setType() call to prevent race conditions
    if (this.#setTypeAbortController) {
      this.#setTypeAbortController.abort();
    }
    this.#setTypeAbortController = new AbortController();
    const signal = this.#setTypeAbortController.signal;

    // Load the visualizer class (may need to fetch from storage)
    const VisualizerClass = await VisualizerRegistry.load(typeName, source);

    // Check if cancelled during load
    if (signal.aborted) {
      console.log(`[VisualizerManager] setType("${typeName}") cancelled during load`);
      return false;
    }

    if (!VisualizerClass) {
      console.error(`Visualization type "${typeName}" not found`);
      return false;
    }

    // Determine context type: explicit contextType, or infer from isWebGL, or default to '2d'
    const newContextType = VisualizerClass.contextType ||
      (VisualizerClass.isWebGL === true ? 'webgl' : '2d');

    // Track previous type for callback
    const previousType = this.currentType;

    // Dispose of current visualizer (important for WebGL/Three.js)
    if (this.currentVisualizer && this.currentVisualizer.dispose) {
      this.currentVisualizer.dispose();
    }

    // Check again after dispose (another setType may have started)
    if (signal.aborted) {
      console.log(`[VisualizerManager] setType("${typeName}") cancelled after dispose`);
      return false;
    }

    // We need a fresh canvas when:
    // 1. Switching between different context types (2d <-> webgl <-> webgpu)
    // 2. Current context is WebGL - because dispose() calls loseContext() which
    //    permanently invalidates the canvas for future WebGL use
    // 3. Current context is WebGPU - renderer.dispose() also invalidates the context
    const needsNewCanvas = this.currentType !== null && (
      newContextType !== this.currentContextType ||
      this.currentContextType === 'webgl' ||
      this.currentContextType === 'webgpu'
    );
    if (needsNewCanvas) {
      this.replaceCanvas();
    }

    // Final abort check before creating visualizer
    if (signal.aborted) {
      console.log(`[VisualizerManager] setType("${typeName}") cancelled before instantiation`);
      return false;
    }

    this.currentContextType = newContextType;

    // Create new visualizer instance
    try {
      this.currentVisualizer = new VisualizerClass(this.canvas, this.config);
      this.currentType = typeName;
    } catch (error) {
      console.error(`[VisualizerManager] Failed to initialize ${typeName}:`, error);
      this.currentVisualizer = null;
      this.currentType = null;
      this.currentContextType = null;
      throw error;
    }

    // Notify listeners of visualizer change (e.g., to reset audio feature state)
    if (this._onVisualizerChange && previousType !== typeName) {
      this._onVisualizerChange(typeName, previousType);
    }

    return true;
  }

  /**
   * Replace the canvas element with a fresh one
   * (required when switching between 2D and WebGL contexts)
   */
  replaceCanvas() {
    const oldCanvas = this.canvas;
    const newCanvas = document.createElement('canvas');
    newCanvas.id = oldCanvas.id;
    // Use logical dimensions, not scaled canvas dimensions
    newCanvas.width = this.logicalWidth;
    newCanvas.height = this.logicalHeight;
    newCanvas.className = oldCanvas.className;

    oldCanvas.parentNode.replaceChild(newCanvas, oldCanvas);
    this.canvas = newCanvas;
  }

  /**
   * Get the current visualizer instance
   */
  getVisualizer() {
    return this.currentVisualizer;
  }

  /**
   * Get the current canvas element
   */
  getCanvas() {
    return this.canvas;
  }

  /**
   * Get current visualization type
   */
  getType() {
    return this.currentType;
  }

  /**
   * Update configuration on current visualizer
   */
  setConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    if (this.currentVisualizer) {
      this.currentVisualizer.setConfig(newConfig);
    }
  }

  /**
   * Get current config
   */
  getConfig() {
    return this.currentVisualizer ? this.currentVisualizer.config : this.config;
  }

  /**
   * Draw a frame using the current visualizer
   * @param {Uint8Array} frequencyData - Raw FFT frequency data
   * @param {AudioFeatures} [features] - Optional enhanced audio features (lazy evaluated)
   */
  draw(frequencyData, features = null) {
    if (this.currentVisualizer) {
      this.currentVisualizer.draw(frequencyData, features);
    }
  }

  /**
   * Clear the canvas
   */
  clear() {
    if (this.currentVisualizer) {
      this.currentVisualizer.clear();
    }
  }

  /**
   * Get the configuration UI for the current visualizer
   */
  getConfigUI() {
    if (!this.currentType) return [];
    const VisualizerClass = VisualizerRegistry.get(this.currentType);
    return VisualizerClass?.getConfigUI ? VisualizerClass.getConfigUI() : [];
  }

  /**
   * Pause the visualizer (used during export to free GPU resources)
   */
  pause() {
    if (this.currentVisualizer && this.currentVisualizer.pause) {
      this.currentVisualizer.pause();
    }
  }

  /**
   * Resume the visualizer after pause
   */
  resume() {
    if (this.currentVisualizer && this.currentVisualizer.resume) {
      this.currentVisualizer.resume();
    }
  }

  /**
   * Suspend the visualizer and release ALL GPU resources.
   * Fully disposes the visualizer - use for long operations like export.
   * Call restore() to recreate the visualizer.
   */
  async suspend() {
    // Always save the current type for restoration, even if visualizer is null
    // This ensures restore() can recreate the correct visualizer type
    this._suspendedType = this.currentType;
    this._suspendedConfig = this.currentVisualizer?.config
      ? { ...this.currentVisualizer.config }
      : { ...this.config };

    // If no visualizer to dispose, we're done (but state is saved)
    if (!this.currentVisualizer) return;

    // Check if this is a WebGPU visualizer (canvas holds GPU context)
    const isWebGPU = this.currentVisualizer.constructor.contextType === 'webgpu';

    // Fully dispose the visualizer
    if (this.currentVisualizer.dispose) {
      this.currentVisualizer.dispose();
    }
    this.currentVisualizer = null;

    // For WebGPU visualizers, replace the canvas to fully release GPU context
    // The canvas element itself retains the WebGPU context even after renderer disposal
    if (isWebGPU) {
      this.replaceCanvas();
    }
  }

  /**
   * Restore the visualizer after suspend.
   * Recreates the visualizer with the same type and config.
   */
  async restore() {
    if (!this._suspendedType) return;

    const type = this._suspendedType;
    const config = this._suspendedConfig;

    // Recreate the visualizer — only clear suspended state on success
    try {
      await this.setType(type);
      if (config) {
        this.setConfig(config);
      }
      this._suspendedType = null;
      this._suspendedConfig = null;
    } catch (err) {
      console.error('[VisualizerManager] restore() failed, suspended state preserved:', err);
    }
  }
}

// Backward compatibility: expose BarVisualizer as alias after bars.js loads
// This ensures exporter.js can still use BarVisualizer
if (typeof window !== 'undefined') {
  Object.defineProperty(window, 'BarVisualizer', {
    get() {
      return VisualizerRegistry.get('bars');
    }
  });
}

// ES Module exports
export { VisualizerRegistry, VisualizerManager, AccessDeniedError };
